import {createVariantActions} from '@features/ai/prompt/common/variants';
import {z} from 'zod';
import {feedbackAttachment} from './feedbackAttachment';

const figma = z.object({
  type: z.literal('figma'),

  sourceUrl: z.string(),
  html: z.string(),
  screenshotUrl: z.string().nullable(),
  lastModified: z.string(),
});

const referenceImage = z.object({
  type: z.literal('referenceImage'),

  url: z.string(),
  isBrandAsset: z.boolean().optional(),
});

const htmlSelection = z.object({
  type: z.literal('htmlSelection'),
  selectedHtml: z.string(),
  selectedContext: z.string().optional(),
  selectedSelector: z.string().optional(),
});

const previousScreen = z.object({
  type: z.literal('previousScreen'),

  screenId: z.string(),
  buttonText: z.string(),
  buttonHtml: z.string(),
  isIconOnly: z.boolean().optional(),
  // GenType of the source screen's active structure (for correct system prompt selection)
  sourceGenType: z.enum(['web-html-css', 'mobile-html-css']).optional(),
});

const screenMention = z.object({
  type: z.literal('screenMention'),

  screenPath: z.string(),
});

const numVariants = z.object({
  type: z.literal('numVariants'),

  numVariants: z.number().min(1).max(10),
});

const createVariant = z.object({
  type: z.literal('createVariant'),
  variant: z.enum(createVariantActions),
  variantPrompt: z
    .string()
    .describe(
      'We only use this to keep track what the prompt was at the time it was sent. Should be ignored.',
    ),
  sourceScreenId: z.string(),
  screenshotUrl: z.string().optional(),
});

const clarifyingQuestions = z.object({
  type: z.literal('clarifyingQuestions'),
  questions: z.array(
    z.object({
      id: z.string(),
      question: z.string(),
      options: z.array(z.string()),
      maxSelections: z.number().default(1),
    }),
  ),
  answers: z.array(
    z
      .union([
        z.object({
          questionId: z.string(),
          questionText: z.string(),
          selectedOption: z.string().nullable(),
          customText: z.string().nullable(),
        }),
        z.object({
          questionId: z.string(),
          questionText: z.string(),
          selectedOptions: z.array(z.string()),
          customText: z.string().nullable(),
        }),
      ])
      .transform((val) => {
        if ('selectedOption' in val) {
          const {selectedOption, ...rest} = val;
          return {
            ...rest,
            selectedOptions: selectedOption ? [selectedOption] : [],
          };
        }
        return val;
      }),
  ),
  skipped: z.boolean(),
});

export const messageAttachment = z.union([
  figma,
  referenceImage,
  htmlSelection,
  previousScreen,
  screenMention,
  numVariants,
  feedbackAttachment,
  createVariant,
  clarifyingQuestions,
]);

export type MessageAttachment = z.infer<typeof messageAttachment>;
